import React, { useEffect, useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import {
  sectionPadding,
  pageTopPadding,
  CONTENT_MAX_WIDE,
  CONTENT_MAX_NARROW,
  CONTENT_INSET,
} from '../styles/layout';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT_MUTED = '#F5F0E8';
const TEXT_BODY = '#F5F0E8';
const GOLD_BORDER = '1px solid rgba(201, 168, 76, 0.2)';
const GOLD_BORDER_SUBTLE = '1px solid rgba(201, 168, 76, 0.15)';

const sections = [
  {
    title: 'Information We Collect',
    content: `When you book with Digital Divot, we collect information necessary to process and confirm your reservation. This includes your full name, email address, and phone number provided during the booking process. We also collect payment information, though card details are handled exclusively by Stripe and never stored on our servers. Additionally, Firebase Analytics collects anonymized usage data such as pages visited, session duration, and device type to help us improve your experience.`,
  },
  {
    title: 'How We Use Your Information',
    content: `Your information is used solely to operate and improve Digital Divot. Specifically, we use it to process and confirm your bookings, send booking confirmation and reminder emails, respond to customer support inquiries, and analyze site usage to enhance our service. We do not use your information for advertising purposes or sell it to third parties.`,
  },
  {
    title: 'Payment Processing',
    content: `All payments are processed securely through Stripe, a PCI-compliant payment processor. When you enter your payment details, that information goes directly to Stripe — Digital Divot never sees, stores, or has access to your full card number, CVV, or billing details. Stripe's privacy policy governs the handling of your payment data and can be reviewed at stripe.com/privacy.`,
  },
  {
    title: 'Cookies & Analytics',
    content: `We use Firebase Analytics to understand how visitors interact with our website. This service uses cookies — small text files stored in your browser — to collect anonymized data about your session. This data helps us understand which pages are most useful, how long sessions last, and where users encounter friction. You can disable cookies through your browser settings, though some features of the site may not function as intended.`,
  },
  {
    title: 'Data Sharing',
    content: `We do not sell, rent, or trade your personal information. We share your data only with the third-party services required to operate Digital Divot — specifically Stripe for payment processing and Firebase (Google) for hosting, authentication, database, and analytics. Both services operate under their own privacy policies and comply with applicable data protection regulations.`,
  },
  {
    title: 'Your Rights',
    content: `You have the right to access, correct, or request deletion of the personal data we hold about you. If you would like to exercise any of these rights, please reach out to us directly at the contact information below. We will respond to all requests within a reasonable timeframe and in accordance with applicable law.`,
  },
  {
    title: 'Policy Updates',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we do, we will update the date at the bottom of this page. We encourage you to review this policy periodically. Continued use of our site after any changes constitutes your acceptance of the updated policy.`,
  },
];

const Privacy = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div
      style={{
        paddingTop: pageTopPadding(isMobile),
        backgroundColor: BG,
        color: '#F5F0E8',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Hero Section */}
      <section
        style={{
          backgroundColor: BG,
          padding: sectionPadding(isMobile),
          borderBottom: GOLD_BORDER_SUBTLE,
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              color: GOLD,
              fontSize: '0.75rem',
              fontWeight: '600',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              marginBottom: '0.5rem',
            }}
          >
            Legal
          </p>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
              fontWeight: '800',
              marginBottom: '0.75rem',
              color: '#F5F0E8',
            }}
          >
            Privacy Policy
          </h1>
          <p
            style={{
              fontSize: '1.125rem',
              color: TEXT_MUTED,
              maxWidth: CONTENT_MAX_NARROW,
              margin: '0 auto',
            }}
          >
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section
        style={{
          padding: sectionPadding(isMobile),
          backgroundColor: BG,
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <p
            style={{
              color: TEXT_BODY,
              fontSize: '1.125rem',
              lineHeight: 1.75,
              margin: 0,
              paddingLeft: '1.25rem',
              borderLeft: `4px solid ${GOLD}`,
            }}
          >
            Digital Divot Golf Simulators is committed to protecting your privacy.
            This policy explains what information we collect, how we use it, and
            your rights regarding your data. By using our website or booking our
            services, you agree to the practices described below.
          </p>
        </div>
      </section>

      {/* Policy Sections */}
      <section
        style={{
          padding: sectionPadding(isMobile),
          backgroundColor: BG,
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {sections.map((section, index) => (
            <div
              key={index}
              style={{
                backgroundColor: CARD_BG,
                borderRadius: '16px',
                border: GOLD_BORDER,
                padding: '1.5rem',
                boxSizing: 'border-box',
                maxWidth: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                }}
              >
                <div
                  style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    backgroundColor: GOLD,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      color: '#000000',
                      fontWeight: '700',
                      fontSize: '0.875rem',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      color: GOLD,
                      marginBottom: '0.75rem',
                      marginTop: 0,
                    }}
                  >
                    {section.title}
                  </h2>
                  <p
                    style={{
                      color: TEXT_MUTED,
                      lineHeight: 1.75,
                      fontSize: '1rem',
                      margin: 0,
                    }}
                  >
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section
        style={{
          padding: sectionPadding(isMobile),
          backgroundColor: BG,
        }}
      >
        <div
          style={{
            maxWidth: CONTENT_MAX_WIDE,
            margin: '0 auto',
            padding: `0 ${CONTENT_INSET}`,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              backgroundColor: CARD_BG,
              border: GOLD_BORDER,
              borderRadius: '16px',
              padding: '2rem',
              boxSizing: 'border-box',
            }}
          >
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: GOLD,
                marginBottom: '0.5rem',
                marginTop: 0,
              }}
            >
              Contact Us
            </h2>
            <p
              style={{
                color: TEXT_MUTED,
                fontSize: '1rem',
                marginBottom: '1.5rem',
                lineHeight: 1.6,
              }}
            >
              For any privacy-related questions or requests, reach out to us directly.
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              <a
                href="mailto:admin@digitaldivotgolf.com"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  fontSize: '1.125rem',
                  color: TEXT_BODY,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = TEXT_BODY;
                }}
              >
                <Mail size={20} style={{ color: GOLD, flexShrink: 0 }} />
                admin@digitaldivotgolf.com
              </a>
              <a
                href="tel:4809233349"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  fontSize: '1.125rem',
                  color: TEXT_BODY,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = TEXT_BODY;
                }}
              >
                <Phone size={20} style={{ color: GOLD, flexShrink: 0 }} />
                480-923-3349
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
